import React, { forwardRef, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { axios } from 'api'

import { Box, Flex, Image, NameList, P, ResponsiveSVG } from 'ui/acro'
import {
  CardSubtitle,
  CardTitle,
  TitleContainer,
  SubStyleChip
} from '../styles'
import heartSVG from '../../../../../assets/images/heart.svg'
import heartFilledSVG from '../../../../../assets/images/heartFilled.svg'
import PlaylistNew from '../../../../../assets/images/PlaylistNew.svg'
import PlaylistFilled from '../../../../../assets/images/PlaylistFilled.svg'
import TimeCodeBadge from './TimeCodeBadge'
import { storeEventInLocalStorage } from 'gtm/pushEvents'
import { LightTheme } from 'ui/bend'
import moment from 'moment'
import { AlomovesIconSVG } from 'images/latest'
import PlayListDrawer from '../../../../components_v2/PlayListDrawer/PlayListDrawer'
import SubCategoryChipList from './SubCategoryChipList'

const HeartIconBox = styled(Box)`
  svg {
    path {
      &.heart-icon {
        fill: #3b3b3b99;
      }
    }
  }
`
const Main = styled(Flex)`
  .no-image {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #f2f2f2;
    aspect-ratio: 16/9;
  }
  .class-image {
    width: 100%;
  }
`
const ClassIndex = styled(Box)`
  width: 32px;
  background: #fff;
  color: #000;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 3;
  text-align: center;
  padding: 5px;

  font-family: 'proxima-nova';
  font-size: 18px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.9px;
`

const getLocalDate = (d) => {
  let date = new Date(d)
  if (moment(date).isValid()) {
    return moment(date).local().format('MM/DD/YYYY')
  }

  date = String(d).replace(/-/g, '/')
  if (moment(date).isValid()) {
    return moment(date).local().format('MM/DD/YYYY')
  }

  return String(d)
}

// this version of the class card is used on the Most Popular and New Classes HP carousels
const ClassCard = forwardRef(
  (
    {
      carouselItem,
      humanDuration,
      id,
      plan,
      videoThumbnail,
      title,
      planEntryUrl,
      favoriteCallback,
      eventName,
      position,
      location,
      isFevIcon = true,
      difficultyLevel,
      className = '',
      subcategories,
      showAllMetaData = true,
      cardIndex,
      onPlaylistIconClick,
      ...rest
    },
    ref
  ) => {
    let image
    //const [isFavoritesAvailable, setIsFavoritesAvailable] = useState(rest.favorites?.length)
    const [favoritesId, setFavoritesId] = useState(rest?.favorites?.[0]?.id)
    const [playlistIds, setPlaylistIds] = useState(rest?.playlistIds)
    const [isDrawerOpen, setDrawerOpen] = useState(false)

    useEffect(() => {
      setPlaylistIds(rest?.playlistIds)
    }, [rest?.playlistIds])

    const singleClass = Boolean(plan?.singleClass)
    const filteredSubStyles = subcategories?.filter(
      (subStyle) => subStyle.slug != 'single-classes'
    )
    if (typeof videoThumbnail !== 'undefined') {
      image = videoThumbnail
    }
    const addClass = (id) => {
      setFavoritesId(id)
      return axios
        .post('/favorites', { plan_entry_id: id })
        .then((res) => {
          if (res.status === 200) {
            setFavoritesId(res?.data?.id || null)
            favoriteCallback?.()
          } else {
            setFavoritesId(null)
          }
        })
        .catch((error) => {
          setFavoritesId(null)
          console.error(error)
        })
    }

    const removeClass = (id) => {
      setFavoritesId(null)
      return axios
        .delete(`/favorites/${id}`)
        .then((res) => {
          if (res.status === 200) {
            setFavoritesId(null)
            favoriteCallback?.()
          } else {
            setFavoritesId(id)
          }
        })
        .catch((error) => {
          console.error(error)
          setFavoritesId(id)
        })
    }

    const pushEvent = (e) => {
      if (!eventName) {
        return
      }
      const coachNames = plan.coaches
        ?.map((item) => `${item.firstName} ${item.lastName}`)
        .join(', ')

      const event_data = {
        event: eventName,
        position,
        instructor: coachNames,
        class: title,
        ...(location && { location })
      }

      storeEventInLocalStorage(event_data)
    }

    const openPlaylistDrawer = () => {
      setDrawerOpen(true)
    }

    const difficulty = difficultyLevel || plan?.difficultyLevel

    return (
      <>
        <Main
          ref={ref}
          id={id}
          flexDirection='column'
          position='relative'
          className={`${className} ${
            carouselItem ? 'swiper-slide unstyled' : 'unstyled'
          }`}
          style={{ minWidth: carouselItem ? '212px' : '173px' }}
          as='a'
          href={planEntryUrl}
          onClick={pushEvent}
        >
          {cardIndex && <ClassIndex>{cardIndex}</ClassIndex>}
          {isFevIcon && (
            <HeartIconBox
              zIndex={'1'}
              right={['5px', '6px', '6px', '4px']}
              top={['5px', '7px', '7px', '4px']}
              position={'absolute'}
              width={'32px'}
              height={'32px'}
              display={'flex'}
              borderRadius={'50%'}
              alignItems={'center'}
              justifyContent={'center'}
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                !favoritesId ? addClass(id) : removeClass(favoritesId)
              }}
              backgroundColor={'#FFFFFF99'}
            >
              <ResponsiveSVG
                SVG={favoritesId ? heartFilledSVG : heartSVG}
                style={{
                  cursor: 'pointer'
                }}
                width='24px'
                height='24px'
              />
            </HeartIconBox>
          )}
          {image ? (
            <Box position='relative' mb={['8px', '10px']}>
              <Image
                src={image}
                objectFit='cover'
                position='absolute'
                top='0'
                left='0'
                style={{ aspectRatio: '16/9' }}
                className='class-image'
              />
              <TimeCodeBadge
                fontSize='13px !important'
                bottom='16px !important'
                left='16px !important'
              >
                {humanDuration}
              </TimeCodeBadge>
            </Box>
          ) : (
            <Box mb={['8px', '10px']} className='no-image'>
              <ResponsiveSVG
                width={['68px', '68px', '130px', '130px']}
                height={['68px', '68px', '130px', '130px']}
                SVG={AlomovesIconSVG}
                className='no-thumbnail'
              />
            </Box>
          )}
          <TitleContainer>
            {title && (
              <CardTitle id='SeriesTitle' as='div' paddingBottom='6px'>
                {title}
              </CardTitle>
            )}
            {isFevIcon && (
              <HeartIconBox
                zIndex={'1'}
                width={'32px'}
                height={'32px'}
                display={'flex'}
                borderRadius={'50%'}
                alignItems={'center'}
                justifyContent={'center'}
                onClick={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                  onPlaylistIconClick()
                }}
                backgroundColor={'#FFFFFF99'}
              >
                <ResponsiveSVG
                  SVG={playlistIds.length ? PlaylistFilled : PlaylistNew}
                  style={{
                    cursor: 'pointer'
                  }}
                  width='24px'
                  height='24px'
                />
              </HeartIconBox>
            )}
          </TitleContainer>

          {plan.coaches && (
            <CardSubtitle as='div'>
              <NameList people={plan.coaches} />
            </CardSubtitle>
          )}
          <CardSubtitle as='div' marginTop='4px'>
            {difficulty && `${difficulty}`}
          </CardSubtitle>
          <CardSubtitle
            color={`${LightTheme.colors.grey5} !important`}
            as='div'
            marginTop='4px'
            lineHeight='18px !important'
          >
            {!singleClass && showAllMetaData && (
              <>
                Series:{' '}
                <a
                  style={{
                    textDecoration: 'underline',
                    color: '#636363'
                  }}
                  {...(plan?.slug ? { href: `/series/${plan?.slug}` } : {})}
                >
                  {plan?.title}
                </a>
              </>
            )}
            {rest?.completedAt ? (
              <P fontSize='14px' color='#758E6D'>
                Completed: {getLocalDate(rest?.completedAt)}
              </P>
            ) : null}
          </CardSubtitle>
          {filteredSubStyles && (
            <SubCategoryChipList subcategories={filteredSubStyles} />
          )}
        </Main>
      </>
    )
  }
)

ClassCard.propTypes = {
  duration: PropTypes.string,
  id: PropTypes.number,
  plan: PropTypes.object,
  title: PropTypes.string.isRequired,
  videoThumbnail: PropTypes.string.isRequired,
  planEntryUrl: PropTypes.string.isRequired
}

export default ClassCard
